/**
 * Client for communicating with the offline store worker.
 * Provides a promise-based API for database operations.
 */
export class OfflineWorkerClient {
    private worker: Worker | null = null;
    private messageId = 0;
    private pending = new Map<number, { resolve: (value: unknown) => void; reject: (error: Error) => void }>();
    private readyPromise: Promise<void>;
    private readyResolve!: () => void;
    private _isInitialized = false;
    private _sqliteImportPath: string = '';

    constructor() {
        this.readyPromise = new Promise((resolve) => {
            this.readyResolve = resolve;
        });
    }

    /**
     * Whether the worker and database are initialized.
     */
    get isInitialized(): boolean {
        return this._isInitialized;
    }

    /**
     * Load the worker from the given URL and initialize the database.
     * 
     * @param workerUrl - URL to the worker script
     * @param sqliteImportPath - URL to the sqlite-wasm module
     * @param prefix - Database file prefix
     * @returns Whether the database needs to be restored from backup
     */
    async init(workerUrl: string, sqliteImportPath: string, prefix: string): Promise<{ needsRestore: boolean }> {
        if (this.worker) {
            throw new Error('Worker already initialized');
        }

        this._sqliteImportPath = sqliteImportPath;
        this.worker = new Worker(workerUrl, { type: 'module' });
        
        this.worker.onmessage = (e) => this._handleMessage(e);
        this.worker.onerror = (e) => this._handleError(e);

        // Wait for worker to signal ready
        await this.readyPromise;

        // Initialize the database
        const result = await this._call<{ success: true; needsRestore: boolean }>('init', { sqliteImportPath, prefix });
        this._isInitialized = true;
        
        return { needsRestore: result.needsRestore };
    }

    private _handleMessage(e: MessageEvent): void {
        const { id, result, error, type } = e.data;

        // Handle ready signal
        if (type === 'ready') {
            this.readyResolve();
            return;
        }

        // Handle response to a call
        const pending = this.pending.get(id);
        if (pending) {
            this.pending.delete(id);
            if (error) {
                pending.reject(new Error(error));
            } else {
                pending.resolve(result);
            }
        }
    }

    private _handleError(e: ErrorEvent): void {
        console.error('Offline worker error:', e.message);
        
        // Reject all pending calls
        for (const [id, { reject }] of this.pending) {
            reject(new Error(`Worker error: ${e.message}`));
            this.pending.delete(id);
        }
    }

    private _call<T = unknown>(method: string, args: Record<string, unknown> = {}): Promise<T> {
        if (!this.worker) {
            return Promise.reject(new Error('Worker not initialized'));
        }

        return new Promise((resolve, reject) => {
            const id = ++this.messageId;
            this.pending.set(id, { 
                resolve: resolve as (value: unknown) => void, 
                reject 
            });
            this.worker!.postMessage({ id, method, args });
        });
    }

    /**
     * Execute a SQL statement and return all result rows.
     */
    async exec<T = Record<string, unknown>>(sql: string, bind?: unknown[]): Promise<T[]> {
        return this._call<T[]>('exec', { sql, bind });
    }

    /**
     * Execute a SQL statement and return a single scalar value.
     */
    async execScalar<T = unknown>(sql: string, bind?: unknown[]): Promise<T> {
        return this._call<T>('execScalar', { sql, bind });
    }

    /**
     * Execute a SQL statement without returning results.
     */
    async run(sql: string, bind?: unknown[]): Promise<void> {
        await this._call('run', { sql, bind });
    }

    /**
     * Delete all data from the database.
     */
    async deleteAll(): Promise<void> {
        await this._call('deleteAll');
    }

    /**
     * Check if database has uncommitted changes that need backup.
     */
    async checkDirty(): Promise<boolean> {
        const result = await this._call<{ isDirty: boolean }>('checkDirty');
        return result.isDirty;
    }

    /**
     * Prepare a backup snapshot in OPFS.
     * Returns success and backup filename, or failure reason.
     */
    async prepareBackup(): Promise<{ success: true; backupFilename: string } | { success: false; reason: string }> {
        return this._call('prepareBackup');
    }

    /**
     * Get the size of the prepared backup file.
     */
    async getBackupSize(): Promise<number> {
        const result = await this._call<{ size: number }>('getBackupSize');
        return result.size;
    }

    /**
     * Stream a chunk of the backup file.
     * @param offset - Byte offset to start reading from
     * @param chunkSize - Number of bytes to read
     * @returns Chunk data, whether we're done, and bytes read
     */
    async streamBackupChunk(offset: number, chunkSize: number): Promise<{ data: ArrayBuffer; done: boolean; bytesRead: number }> {
        return this._call('streamBackupChunk', { offset, chunkSize });
    }

    /**
     * Clean up the backup file after successful copy.
     */
    async cleanupBackup(): Promise<void> {
        await this._call('cleanupBackup');
    }

    /**
     * Start restoring database from backup.
     * This closes the current database and prepares for writing.
     */
    async startRestore(): Promise<void> {
        await this._call('startRestore');
    }

    /**
     * Write a chunk of restore data.
     * @param data - Chunk of database file to write
     */
    async writeRestoreChunk(data: ArrayBuffer): Promise<number> {
        const result = await this._call<{ success: true; bytesWritten: number }>('writeRestoreChunk', { data });
        return result.bytesWritten;
    }

    /**
     * Finish restore and reopen the database.
     */
    async finishRestore(): Promise<void> {
        await this._call('finishRestore', { sqliteImportPath: this._sqliteImportPath });
    }

    /**
     * Close the database and terminate the worker.
     */
    async close(): Promise<void> {
        if (this.worker) {
            await this._call('close');
            this.worker.terminate();
            this.worker = null;
            this._isInitialized = false;
        }
    }
}
